import type { StreamingTranscriber, TurnEvent } from 'assemblyai';
import { ApiService } from './api.service';
import { floatTo16BitPCM } from '../utils/audio.util';
import { useWorkplaceStore } from '@host/stores/workplace.store';

export interface TranscriptionEvent {
  text: string;
  speaker?: string;
  timestamp: number;
  confidence: number;
  roomPid: string;
  isFinal: boolean;
}

export interface StreamingTranscriptionConfig {
  speakerLabels?: boolean;
  onTranscript?: (event: TranscriptionEvent) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
}

export class AssemblyAIStreamingService {
  private transcriber: StreamingTranscriber | null = null;
  private audioStream: MediaStream | null = null;
  private isRecording = false;
  private roomPid: string | null = null;
  private config: StreamingTranscriptionConfig | null = null;
  private jitsiAudioTrack: any = null;
  private audioContext: AudioContext | null = null;
  private audioProcessor: ScriptProcessorNode | null = null;
  private streamWriter: WritableStreamDefaultWriter<ArrayBufferLike> | null = null;

  private virtualGain: GainNode | null = null;
  private currentSourceNode: MediaStreamAudioSourceNode | null = null;

  // Reconnection state
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000; // 2 seconds
  private isReconnecting = false;
  private shouldReconnect = true;

  constructor() {}

  /**
   * Set the Jitsi audio track to use for transcription
   */
  setJitsiAudioTrack(audioTrack: any): void {
    this.jitsiAudioTrack = audioTrack;
  }

  /**
   * Start streaming transcription for a room
   */
  async startTranscription(roomPid: string, config: StreamingTranscriptionConfig): Promise<void> {
    try {
      // TODO: Check mock data
      if (this.isRecording && !this.isReconnecting) {
        console.warn('Transcription already in progress');
        return;
      }

      if (this.transcriber) {
        return;
      }

      this.isRecording = true;
      this.roomPid = roomPid;
      this.config = config;
      this.shouldReconnect = true; // Enable reconnection attempts

      // Update status
      config.onStatusChange?.('connecting');

      // Get org live caption settings from global store
      let speechModel: 'universal-streaming-english' | 'universal-streaming-multilingual' =
        'universal-streaming-english';

      try {
        const orgSettings = useWorkplaceStore.getState().orgSettings;
        const liveCaptionSettings = orgSettings?.['live-caption-settings'];

        if (liveCaptionSettings?.speechModel) {
          speechModel = liveCaptionSettings.speechModel;
        }
      } catch (error) {
        console.warn('Failed to read live caption settings from store, using defaults:', error);
      }

      const token = await this.getToken();

      // Initialize Assembly AI Realtime Transcriber (lazy-loaded)
      const { StreamingTranscriber } = await import('assemblyai');

      // assemblyai >= 4.27 defaults connectTimeout to 1000ms, which is too short
      // and causes "Streaming connection timed out after 1000ms". Override it with a generous value
      const transcriberOptions = {
        sampleRate: 16_000,
        token: token,
        formatTurns: true,
        websocketBaseUrl: 'wss://streaming.assemblyai.com/v3/ws',
        speechModel: speechModel,
        connectTimeout: 10_000,
      };
      this.transcriber = new StreamingTranscriber(transcriberOptions);

      // Set up event handlers
      this.transcriber.on('turn', (turn: TurnEvent) => {
        this._log(turn);
        if (!turn.transcript) {
          console.warn('No turn');
          return;
        }
        this._log('Turn:', turn.transcript);

        const transcriptionEvent: TranscriptionEvent = {
          isFinal: turn.end_of_turn,
          text: turn.transcript,
          timestamp: Date.now(),
          confidence: turn.end_of_turn_confidence,
          roomPid: this.roomPid!,
        };

        config.onTranscript?.(transcriptionEvent);
      });

      this.transcriber.on('error', async (error: Error) => {
        console.error('Assembly AI transcription error:', error);

        // Check if this is a token expiration error
        const isTokenError = error.message?.includes('token') || error.message?.includes('auth');

        if (
          isTokenError &&
          this.shouldReconnect &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this._log('🔑 Token error detected, attempting to reconnect with fresh token');
          this.reconnectAttempts++;
          this.isReconnecting = true;

          config.onStatusChange?.('connecting');
          await new Promise((resolve) => setTimeout(resolve, this.reconnectDelay));

          try {
            const savedRoomPid = this.roomPid;
            const savedConfig = this.config;
            const savedJitsiTrack = this.jitsiAudioTrack;

            this.cleanupConnection();
            this.jitsiAudioTrack = savedJitsiTrack;

            if (savedRoomPid && savedConfig) {
              await this.startTranscription(savedRoomPid, savedConfig);
              this._log('✅ Reconnection after token error successful');
              this.reconnectAttempts = 0;
              this.isReconnecting = false;
            }
          } catch (reconnectError) {
            this._log('❌ Reconnection after token error failed:', reconnectError);
            this.isReconnecting = false;
            config.onError?.(error);
            config.onStatusChange?.('error');
          }
        } else {
          config.onError?.(error);
          config.onStatusChange?.('error');
        }
      });

      this.transcriber.on('open', (event) => {
        this._log('Open success', event);
      });

      this.transcriber.on('close', async (code, reason) => {
        this._log('Assembly AI connection closed', code, reason);

        // Only attempt reconnection if:
        // 1. Not manually stopped (shouldReconnect flag)
        // 2. Not already reconnecting
        // 3. Haven't exceeded max attempts
        if (
          this.shouldReconnect &&
          !this.isReconnecting &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          this.reconnectAttempts++;
          this.isReconnecting = true;

          this._log(
            `🔄 Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
          );
          config.onStatusChange?.('connecting');

          // Wait before reconnecting
          await new Promise((resolve) => setTimeout(resolve, this.reconnectDelay));

          try {
            // Save current state
            const savedRoomPid = this.roomPid;
            const savedConfig = this.config;
            const savedJitsiTrack = this.jitsiAudioTrack;

            // Clean up old connection
            this.cleanupConnection();

            // Restore state
            this.jitsiAudioTrack = savedJitsiTrack;

            // Attempt to reconnect
            if (savedRoomPid && savedConfig) {
              await this.startTranscription(savedRoomPid, savedConfig);
              this._log('✅ Reconnection successful');
              this.reconnectAttempts = 0; // Reset counter on success
              this.isReconnecting = false;
            }
          } catch (error) {
            this._log(`❌ Reconnection attempt ${this.reconnectAttempts} failed:`, error);
            this.isReconnecting = false;

            // If we've exhausted all attempts, give up
            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
              this._log('❌ Max reconnection attempts reached. Giving up.');
              config.onStatusChange?.('error');
              config.onError?.(new Error('Failed to reconnect after maximum attempts'));
              this.cleanup();
            }
          }
        } else {
          // Manual stop or max attempts reached
          config.onStatusChange?.('disconnected');
          this.cleanup();
        }
      });

      // Connect to Assembly AI
      await this.transcriber.connect();
      config.onStatusChange?.('connected');

      await this.startAudioCapture();

      this.isRecording = true;
      this._log(`Started streaming transcription for room: ${roomPid}`);
    } catch (error) {
      console.error('Failed to start transcription:', error);
      config.onError?.(error as Error);
      config.onStatusChange?.('error');
      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop streaming transcription
   */
  async stopTranscription(): Promise<void> {
    try {
      this._log('Stopping streaming transcription...');

      // Disable reconnection when manually stopping
      this.shouldReconnect = false;

      if (this.transcriber) {
        this._log('stopTranscription - Closing transcriber');
        await this.transcriber.close();
      }

      this.cleanup();
      this.config?.onStatusChange?.('disconnected');

      this._log('Streaming transcription stopped');
    } catch (error) {
      console.error('Error stopping transcription:', error);
      this.cleanup();
    }
  }

  /**
   * Check if transcription is currently active
   */
  isActive(): boolean {
    return this.isRecording;
  }

  /**
   * Get current room PID
   */
  getCurrentRoomPid(): string | null {
    return this.roomPid;
  }

  private async startAudioCapture(): Promise<void> {
    try {
      const sampleRate = 16000;

      this.audioContext = new AudioContext({ sampleRate });

      // Virtual mic (stable)
      this.virtualGain = this.audioContext.createGain();
      this.virtualGain.gain.value = 1.0;

      // AssemblyAI stream
      const transcriberStream = this.transcriber!.stream();
      this.streamWriter = transcriberStream.getWriter();

      // Processor
      this.audioProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.audioProcessor.onaudioprocess = (event) => {
        if (!this.jitsiAudioTrack || this.jitsiAudioTrack?.isMuted()) return;

        const input = event.inputBuffer.getChannelData(0);
        const pcm = floatTo16BitPCM(input);

        const chunkMs = 50;
        const samples = Math.floor((sampleRate * chunkMs) / 1000);
        const bytes = samples * 2;

        for (let i = 0; i < pcm.length; i += bytes) {
          const chunk = pcm.slice(i, i + bytes);
          if (chunk.length === bytes && this.streamWriter) {
            this.streamWriter.write(chunk.buffer).catch(console.error);
          }
        }
      };

      // Wire virtual mic → processor → output
      this.virtualGain.connect(this.audioProcessor);
      this.audioProcessor.connect(this.audioContext.destination);

      // Attach initial Jitsi track if available
      if (this.jitsiAudioTrack) {
        this.attachJitsiTrack(this.jitsiAudioTrack);
      }

      this._log('🎙 Virtual microphone pipeline created');
    } catch (err) {
      console.error('Audio capture failed:', err);
      throw err;
    }
  }

  public attachJitsiTrack(track: any | null): void {
    this._log('🎧 Jitsi Track Info', track);

    if (!this.audioContext || !this.virtualGain) return;

    // Remove old source
    if (this.currentSourceNode) {
      this.currentSourceNode.disconnect();
      this.currentSourceNode = null;
    }

    if (!track) {
      this.jitsiAudioTrack = null;
      this._log('🔇 Jitsi audio detached');
      return;
    }

    const mediaTrack = track.getTrack();
    const stream = new MediaStream([mediaTrack]);

    const source = this.audioContext.createMediaStreamSource(stream);
    source.connect(this.virtualGain);

    this.currentSourceNode = source;
    this.jitsiAudioTrack = track;

    this._log('🔁 Jitsi audio hot-swapped into virtual mic');
  }

  /**
   * Clean up only the connection-related resources
   * Used during reconnection to preserve state like roomPid, config, jitsiAudioTrack
   */
  private cleanupConnection(): void {
    // Clean up audio processing components
    if (this.streamWriter) {
      this.streamWriter.releaseLock();
      this.streamWriter = null;
    }

    if (this.audioProcessor) {
      this.audioProcessor.disconnect();
      this.audioProcessor = null;
    }

    if (this.currentSourceNode) {
      this.currentSourceNode.disconnect();
      this.currentSourceNode = null;
    }

    if (this.virtualGain) {
      this.virtualGain.disconnect();
      this.virtualGain = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Only stop tracks if we created them ourselves (fallback case)
    // Don't stop Jitsi tracks as they're managed by Jitsi
    if (this.audioStream && !this.jitsiAudioTrack) {
      this.audioStream.getTracks().forEach((track) => track.stop());
    }
    this.audioStream = null;

    if (this.transcriber) {
      this.transcriber = null;
    }
  }

  /**
   * Full cleanup including state reset
   */
  private cleanup(): void {
    this.isRecording = false;

    // Clean up connection resources
    this.cleanupConnection();

    // Reset all state
    this.roomPid = null;
    this.config = null;
    this.jitsiAudioTrack = null;

    // Reset reconnection state
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this.shouldReconnect = false;
  }

  /**
   * Get a temporary token from the backend API
   */
  private async getToken(): Promise<string> {
    try {
      this._log('Requesting streaming token from API...');

      const response = await ApiService.get('ai/assembly-ai/streaming-token');

      this._log('Successfully obtained streaming token from API');
      return response.token;
    } catch (error) {
      console.error('Failed to get streaming token from API:', error);
      throw new Error(`Failed to get streaming token: ${error}`);
    }
  }

  private _log(...message: any) {
    console.log('[AssemblyAIStreamingService]', ...message);
  }
}

// Singleton instance
export const assemblyAIStreamingService = new AssemblyAIStreamingService();
